if (!customElements.get('header-menu')) {
  const CLOSE_DELAY = 120;
  const OPEN_DELAY = 90;
  const DRAWER_BREAKPOINT = '(min-width: 900px)';

  class HeaderMenu extends HTMLElement {
    #closeTimer = null;
    #openTimer = null;
    #open = null;
    #hovered = false;

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-menu-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-menu-panel]'));
      this.closers = Array.from(this.querySelectorAll('[data-menu-close]'));
      this.tabs = Array.from(this.querySelectorAll('[data-menu-tab]'));

      for (const tab of this.tabs) {
        tab.addEventListener('click', this.#onTabClick);
        tab.addEventListener('keydown', this.#onTabKeydown);
      }

      this.#initDrawer();

      if (this.triggers.length === 0) return;

      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      for (const trigger of this.triggers) {
        trigger.setAttribute('role', 'button');
        trigger.addEventListener('click', this.#onTriggerClick);
        trigger.addEventListener('keydown', this.#onTriggerKeydown);

        if (this.canHover) {
          trigger.addEventListener('mouseenter', this.#onTriggerEnter);
          trigger.addEventListener('mouseleave', this.#cancelOpen);
        }
      }

      if (this.canHover) {
        for (const closer of this.closers) {
          closer.addEventListener('mouseenter', this.#scheduleClose);
        }

        this.addEventListener('mouseenter', this.#cancelClose);
        this.addEventListener('mouseleave', this.#scheduleClose);
      }

      for (const panel of this.panels) {
        panel.addEventListener('keydown', this.#onPanelKeydown);
      }

      this.addEventListener('focusout', this.#onFocusOut);
      document.addEventListener('keydown', this.#onKeydown);
      document.addEventListener('click', this.#onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#onKeydown);
      document.removeEventListener('click', this.#onDocumentClick);
      this.#desktop?.removeEventListener('change', this.#onBreakpointChange);
      this.#cancelClose();
      this.#cancelOpen();
    }

    #initDrawer() {
      this.drawer = this.querySelector('[data-drawer]');
      if (!this.drawer) return;

      this.drawerOpener = this.querySelector('[data-drawer-open]');
      if (this.drawerOpener?.tagName === 'A') {
        this.drawerOpener.setAttribute('role', 'button');
        this.drawerOpener.setAttribute('aria-haspopup', 'dialog');
        this.drawerOpener.addEventListener('keydown', this.#pressOnSpace);
      }

      this.drawerOpener?.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.drawer.open) return;
        this.drawer.showModal();
        this.drawer
          .querySelector('[data-level][data-active]')
          ?.focus({ preventScroll: true });
      });

      for (const close of this.querySelectorAll('[data-drawer-close]')) {
        close.addEventListener('click', () => this.drawer.close());
      }

      this.#desktop = window.matchMedia(DRAWER_BREAKPOINT);
      this.#desktop.addEventListener('change', this.#onBreakpointChange);
    }

    #desktop = null;

    #pressOnSpace = (event) => {
      if (event.key !== ' ') return;
      event.preventDefault();
      event.currentTarget.click();
    };

    #onBreakpointChange = (event) => {
      if (event.matches && this.drawer?.open) this.drawer.close();
    };

    get open() {
      return this.#open;
    }

    set open(name) {
      this.#cancelOpen();

      if (this.#open === name) return;
      this.#open = name;
      this.#hovered = false;

      for (const trigger of this.triggers) {
        trigger.setAttribute('aria-expanded', String(trigger.dataset.menuTrigger === name));
      }

      for (const panel of this.panels) {
        panel.toggleAttribute('hidden', panel.dataset.menuPanel !== name);
      }
    }

    #panelFor(name) {
      return this.panels.find((panel) => panel.dataset.menuPanel === name) ?? null;
    }

    #triggerFor(name) {
      return this.triggers.find((trigger) => trigger.dataset.menuTrigger === name) ?? null;
    }

    #focusablesIn(panel) {
      return Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
    }

    #selectTab(tab) {
      for (const other of this.tabs) {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        document.getElementById(other.getAttribute('aria-controls'))?.toggleAttribute('hidden', !selected);
      }
    }

    #onTabClick = (event) => {
      this.#selectTab(event.currentTarget);
    };

    #onTabKeydown = (event) => {
      const count = this.tabs.length;
      const index = this.tabs.indexOf(event.currentTarget);
      const targets = {
        ArrowRight: (index + 1) % count,
        ArrowLeft: (index - 1 + count) % count,
        Home: 0,
        End: count - 1,
      };
      if (!(event.key in targets)) return;

      event.preventDefault();
      const next = this.tabs[targets[event.key]];
      this.#selectTab(next);
      next.focus();
    };

    #cancelClose = () => {
      if (this.#closeTimer === null) return;
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    };

    #cancelOpen = () => {
      if (this.#openTimer === null) return;
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    };

    #scheduleClose = () => {
      this.#cancelClose();
      this.#cancelOpen();
      this.#closeTimer = setTimeout(() => {
        this.#closeTimer = null;
        if (this.#keyboardInside()) return;
        this.open = null;
      }, CLOSE_DELAY);
    };

    #onTriggerEnter = (event) => {
      this.#cancelClose();
      this.#cancelOpen();

      const name = event.currentTarget.dataset.menuTrigger;

      if (this.#open !== null) {
        if (this.#open !== name && !this.#keyboardInside()) {
          this.open = name;
          this.#hovered = true;
        }
        return;
      }

      this.#openTimer = setTimeout(() => {
        this.#openTimer = null;
        this.open = name;
        this.#hovered = true;
      }, OPEN_DELAY);
    };

    #keyboardInside() {
      const panel = this.#open === null ? null : this.#panelFor(this.#open);
      const active = document.activeElement;
      return Boolean(panel?.contains(active) && active.matches(':focus-visible'));
    }

    #onTriggerClick = (event) => {
      event.preventDefault();
      this.#cancelClose();

      const name = event.currentTarget.dataset.menuTrigger;

      if (this.#open === name && this.#hovered && event.detail > 0) {
        this.#hovered = false;
        return;
      }

      this.open = this.#open === name ? null : name;
    };

    #onTriggerKeydown = (event) => {
      if (event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
        return;
      }

      if (event.key !== 'Tab' || event.shiftKey) return;

      const name = event.currentTarget.dataset.menuTrigger;
      if (this.#open !== name) return;

      const panel = this.#panelFor(name);
      const [first] = panel ? this.#focusablesIn(panel) : [];
      if (!first) return;

      event.preventDefault();
      first.focus();
    };

    #onKeydown = (event) => {
      if (event.key !== 'Escape' || this.#open === null) return;

      const trigger = this.#triggerFor(this.#open);
      const focusInside = this.contains(document.activeElement);
      this.open = null;
      if (focusInside) trigger?.focus();
    };

    #onPanelKeydown = (event) => {
      if (event.key !== 'Tab' || this.#open === null) return;

      const trigger = this.#triggerFor(this.#open);
      const focusables = this.#focusablesIn(event.currentTarget);
      if (!trigger || focusables.length === 0) return;

      if (event.shiftKey && document.activeElement === focusables[0]) {
        event.preventDefault();
        trigger.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === focusables[focusables.length - 1]) {
        const next = this.#navItemAfter(trigger);
        if (!next) return;

        event.preventDefault();
        this.open = null;
        next.focus();
      }
    };

    #navItemAfter(trigger) {
      const nav = trigger.closest('nav');
      if (!nav) return null;

      const items = Array.from(nav.querySelectorAll('a[href], button:not([disabled])')).filter(
        (item) => item.getClientRects().length > 0
      );

      return items[items.indexOf(trigger) + 1] ?? null;
    }

    #onFocusOut = (event) => {
      if (event.relatedTarget === null) return;
      if (this.contains(event.relatedTarget)) return;
      this.open = null;
    };

    #onDocumentClick = (event) => {
      if (this.#open === null || this.contains(event.target)) return;
      this.open = null;
    };
  }

  customElements.define('header-menu', HeaderMenu);
}
